import type { FleetMachineOverview, FleetProfileState, FleetProfileStatus } from '@immo/api-contract';

/**
 * What a worker of one lane is doing, as the workers panel shows it. The agent's five `state` tokens, plus the two
 * facts the page reads around them: a `waiting-on-human` whose title says `OUT OF SERVICE` (the host's account is
 * logged out or at its usage limit — no person's answer clears it), and a machine the fleet has stopped hearing from.
 */
export type LaneWorkerStatus = FleetProfileState | 'out-of-service' | 'offline';

export type LaneWorker = {
  readonly machineId: string;
  readonly machine: string;
  readonly worker: string | null;
  readonly status: LaneWorkerStatus;
  readonly title: string | null;
  readonly reportedAt: string;
  /** The lane's own report, for the start/stop rule the Fleet page uses (`isStoppedByOperator`). */
  readonly profile: Pick<FleetProfileStatus, 'state' | 'title' | 'fields'>;
};

const OUT_OF_SERVICE = /OUT OF SERVICE/;

/** Working first, then free to take work, then what needs a person, then what is off. */
const ORDER: Record<LaneWorkerStatus, number> = {
  working: 0,
  idle: 1,
  pending: 2,
  'waiting-on-human': 3,
  'out-of-service': 4,
  stopped: 5,
  offline: 6,
};

function statusOf(machine: FleetMachineOverview, state: FleetProfileState, title: string | null): LaneWorkerStatus {
  if (machine.stale) return 'offline';

  if (state === 'waiting-on-human' && title !== null && OUT_OF_SERVICE.test(title)) return 'out-of-service';

  return state;
}

/** Every machine that reports `lane`, one row per report, in {@link ORDER} and then by machine name. */
export function laneWorkers(machines: ReadonlyArray<FleetMachineOverview>, lane: string): ReadonlyArray<LaneWorker> {
  return machines
    .filter((machine) => machine.approval === 'approved')
    .flatMap((machine) =>
      machine.profiles
        .filter((profile) => profile.profile === lane)
        .map((profile): LaneWorker => ({
          machineId: machine.id,
          machine: machine.name,
          worker: profile.worker ?? null,
          status: statusOf(machine, profile.state, profile.title),
          title: profile.title,
          reportedAt: profile.reportedAt,
          profile: { state: profile.state, title: profile.title, fields: profile.fields },
        })),
    )
    .sort((a, b) => ORDER[a.status] - ORDER[b.status] || a.machine.localeCompare(b.machine));
}
